import { get, IHttpRequestConfig, post, put } from "@/browser";
import { extractDataFromResponse, parseResponse } from "@/core/api";
import { compose } from "@/core/url";
import { GetFilePreviewResult } from "@/data/attachments/types/GetFilePreviewResult";

import { Attachment } from "./types";
import { attachmentType, IAttachment, TAttachmentPurpose } from "./types-ensure";
import { RequestUploadData } from "./types/RequestUploadData";

const API = {
  get: "/api/files/{systemId}",
  getById: "/api/file/details/{attachmentId}",
  upload: "/api/files",
  clone: "/api/files/clone/{systemId}",
  getPreview: "/api/files/{fileSystemId}/preview",
  requestUpload: "/api/files/upload",
  acknowledgeUpload: "/api/files/acknowledge/{systemId}",
};

export interface GetAttachmentMetaParams {
  systemId: string;
}

export const getAttachmentMeta = (params: GetAttachmentMetaParams, options?: IHttpRequestConfig): any => {
  const { systemId, ...search } = params;
  const url = compose(API.get, { systemId }, search);

  const cachedItem = localStorage.getItem(systemId);

  if (cachedItem && JSON.parse(cachedItem).validThrough > Date.now()) {
    return extractDataFromResponse(JSON.parse(cachedItem));
  }

  return get<IAttachment>(url, options).then(res => {
    localStorage.setItem(systemId, JSON.stringify({ ...res, validThrough: Date.now() + 110000 }));
    return extractDataFromResponse(res);
  });
};

export interface GetAttachmentMetaByIdParams {
  attachmentId: number;
}

export const getAttachmentMetaById = (params: GetAttachmentMetaByIdParams, options?: IHttpRequestConfig) => {
  const { attachmentId, ...search } = params;
  const url = compose(API.getById, { attachmentId }, search);

  return get<IAttachment>(url, options).then(extractDataFromResponse).then(attachmentType);
};

export interface UploadFileParams {
  file: File;
  type?: string;
  parseAsync?: boolean;
  skipParsing?: boolean;
}

export const uploadFile = (params: UploadFileParams, options?: IHttpRequestConfig) => {
  const payload = new FormData();
  payload.append("file", params.file);

  return post<IAttachment>(
    compose(API.upload, {}, { type: params.type, parseAsync: params.parseAsync, skipParsing: params.skipParsing }),
    payload,
    options,
  )
    .then(extractDataFromResponse)
    .then(attachmentType);
};

export interface CloneFileParams {
  systemId: string;
}

export const cloneFile = (params: CloneFileParams, options?: IHttpRequestConfig) => {
  const { systemId } = params;
  const url = compose(API.clone, { systemId });

  return post<IAttachment>(url, {}, options).then(extractDataFromResponse).then(attachmentType);
};

export interface IGetFilePreviewParams {
  fileSystemId: string;
}

export const getFilePreview = (params: IGetFilePreviewParams, options?: IHttpRequestConfig) => {
  const { fileSystemId } = params;
  const url = compose(API.getPreview, { fileSystemId });

  return get<IAttachment>(url, options).then(parseResponse<GetFilePreviewResult>(GetFilePreviewResult));
};

export const getBlob = (url: string) => {
  return fetch(url, {
    method: "GET",
  }).then(resp => resp.blob());
};

export interface RequestUploadParams {
  type: TAttachmentPurpose;
  fileName: string;
  contentType: string;
  uploadId: string | null;
  partNumber: number | null;
  systemId: string | null;
}

export const requestUpload = (params: RequestUploadParams, options?: IHttpRequestConfig) => {
  const url = compose(API.requestUpload, {}, {});

  return post(url, params, options).then(parseResponse<RequestUploadData>(RequestUploadData));
};

export interface AcknowledgeUploadETag {
  eTag: string;
  partNumber: number;
}

export interface AcknowledgeUploadParams {
  systemId: string;
  uploadId: string;
  eTags: AcknowledgeUploadETag[];
  parsing?: boolean;
}

export const acknowledgeUpload = (params: AcknowledgeUploadParams, options?: IHttpRequestConfig) => {
  const { systemId, uploadId, eTags, parsing } = params;

  const url = compose(API.acknowledgeUpload, { systemId }, { parsing });

  return put(url, { uploadId, eTags }, options).then(parseResponse<Attachment>(Attachment));
};
